if (!customElements.get('native-fbt')) {
  customElements.define('native-fbt', class extends HTMLElement {
    connectedCallback() {
      this.controller = new AbortController();
      this.load();
    }

    disconnectedCallback() {
      this.controller?.abort();
    }

    async load() {
      try {
        const response = await fetch(this.dataset.url, { signal: this.controller.signal });
        if (!response.ok) throw new Error('Recommendation request failed');
        const html = new DOMParser().parseFromString(await response.text(), 'text/html');
        const recommendations = html.querySelector('[data-native-fbt-recommendations]');
        if (!recommendations?.querySelector('[data-native-fbt-card]')) return;
        this.querySelector('[data-native-fbt-recommendations]').replaceChildren(...recommendations.childNodes);
        this.cards = Array.from(this.querySelectorAll('[data-native-fbt-card]'));
        this.style.setProperty('--native-fbt-columns', this.cards.length);
        const options = { signal: this.controller.signal };
        this.addEventListener('change', () => this.update(), options);
        this.querySelector('form').addEventListener('submit', event => this.submit(event), options);
        document.addEventListener('variant:change', event => {
          const formId = event.target?.getAttribute?.('id') || '';
          if (formId.startsWith('ProductForm-') && formId.endsWith(`-${this.dataset.productId}`) &&
              (this.dataset.productFormId ? formId === this.dataset.productFormId : !event.target.closest('quick-view'))) {
            this.syncSource(event.detail?.variant?.id);
          }
        }, { ...options, capture: true });
        const productForm = this.dataset.productFormId
          ? document.getElementById(this.dataset.productFormId)
          : Array.from(document.querySelectorAll(`form[id^="ProductForm-"][id$="-${this.dataset.productId}"]`)).find(form => !form.closest('quick-view'));
        const currentVariant = productForm?.querySelector('[name="id"]');
        this.syncSource(currentVariant?.value);
        this.hidden = false;
      } catch (error) {
        if (error.name !== 'AbortError') this.hidden = true;
      }
    }

    syncSource(id) {
      const select = this.querySelector('[data-source] [data-native-fbt-variant]');
      if (id && Array.from(select.options).some(option => option.value === String(id))) select.value = id;
      this.update();
    }

    money(cents) {
      return theme.Currency.formatMoney(cents, theme.settings.moneyFormat);
    }

    update() {
      let total = 0;
      let compare = 0;
      let count = 0;
      for (const card of this.cards) {
        const option = card.querySelector('[data-native-fbt-variant]').selectedOptions[0];
        const checkbox = card.querySelector('[data-native-fbt-selected]');
        checkbox.disabled = option.dataset.available !== 'true';
        const quantity = Number(option.dataset.quantity) || 1;
        const price = Number(option.dataset.price);
        const comparePrice = Math.max(price, Number(option.dataset.compare) || price);
        card.querySelector('[data-native-fbt-price]').innerHTML = this.money(price);
        const compareElement = card.querySelector('[data-native-fbt-price-compare]');
        compareElement.innerHTML = this.money(comparePrice);
        compareElement.hidden = comparePrice <= price;
        const quantityElement = card.querySelector('[data-native-fbt-quantity]');
        quantityElement.hidden = quantity === 1;
        quantityElement.querySelector('span').textContent = quantity;
        const image = card.querySelector('img');
        if (image && option.dataset.image && image.getAttribute('src') !== option.dataset.image) {
          const maxWidth = Number(option.dataset.imageWidth) || 1440;
          image.srcset = [...[180, 360, 540, 720, 1080, 1440].filter(width => width < maxWidth), maxWidth].map(width => {
            const url = new URL(option.dataset.image, document.baseURI);
            url.searchParams.set('width', width);
            return `${url.href} ${width}w`;
          }).join(', ');
          image.src = option.dataset.image;
        }
        card.querySelectorAll('[data-native-fbt-link]').forEach(link => {
          const url = new URL(link.href);
          url.searchParams.set('variant', option.value);
          link.href = url.href;
        });
        if (checkbox.checked && !checkbox.disabled) {
          count += 1;
          total += price * quantity;
          compare += comparePrice * quantity;
        }
      }
      this.querySelector('[data-native-fbt-total]').innerHTML = this.money(total);
      const compareElement = this.querySelector('[data-native-fbt-compare]');
      compareElement.innerHTML = this.money(compare);
      compareElement.hidden = compare <= total;
      this.querySelector('[data-native-fbt-submit]').disabled = count === 0 || this.busy;
    }

    async submit(event) {
      event.preventDefault();
      if (this.busy) return;
      const items = this.cards.filter(card => {
        const checkbox = card.querySelector('[data-native-fbt-selected]');
        return checkbox.checked && !checkbox.disabled;
      }).map(card => {
        const option = card.querySelector('[data-native-fbt-variant]').selectedOptions[0];
        return { id: Number(option.value), quantity: Number(option.dataset.quantity) || 1 };
      });
      if (!items.length) return;
      this.busy = true;
      const button = this.querySelector('[data-native-fbt-submit]');
      const errorElement = this.querySelector('[data-native-fbt-error]');
      button.setAttribute('aria-busy', 'true');
      errorElement.hidden = true;
      this.update();
      let added = false;
      const cardAnimationRequest = { button };
      document.dispatchEvent(new CustomEvent('k52:cart-animation-start', { detail: cardAnimationRequest }));
      try {
        const sections = [];
        document.documentElement.dispatchEvent(new CustomEvent('cart:bundled-sections', { bubbles: true, detail: { sections } }));
        const response = await fetch(this.dataset.cartAddUrl, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ items, sections, sections_url: window.location.pathname })
        });
        const state = await response.json();
        if (!response.ok || state.status) throw new Error(typeof state.description === 'string' ? state.description : this.dataset.error);
        added = true;
        if (theme.settings.cartType === 'page') {
          window.location.assign(this.dataset.cartUrl);
          return;
        }
        const cartResponse = await fetch(`${this.dataset.cartUrl}.js`, { headers: { Accept: 'application/json' } });
        if (!cartResponse.ok) {
          window.location.assign(this.dataset.cartUrl);
          return;
        }
        const cart = await cartResponse.json();
        cart.sections = state.sections;
        theme.pubsub.publish(theme.pubsub.PUB_SUB_EVENTS.cartUpdate, { source: 'native-fbt', cart });
        document.dispatchEvent(new CustomEvent('ajaxProduct:added', { detail: { product: state } }));
        document.dispatchEvent(new CustomEvent('k52:cart-animation-success', { detail: cardAnimationRequest }));
        const drawer = document.querySelector('cart-drawer');
        if (drawer) drawer.show(button);
        else window.location.assign(this.dataset.cartUrl);
      } catch (error) {
        if (added) {
          window.location.assign(this.dataset.cartUrl);
          return;
        }
        errorElement.textContent = error.message || this.dataset.error;
        errorElement.hidden = false;
      } finally {
        document.dispatchEvent(new CustomEvent('k52:cart-animation-end', { detail: cardAnimationRequest }));
        this.busy = false;
        button.removeAttribute('aria-busy');
        this.update();
      }
    }
  });
}
